import React, { useEffect, useRef } from "react";
import { MemoryRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useStore } from "react-redux";
import NavigationContextReset from "./NavigationContextReset";
import ErrorBoundary from "../../components/ErrorBoundary";
import { openWindow, updateWindowPath } from "../../Slices/windowsSlice";
import { findLeadInLists } from "../../Slices/leadListUtils";

/**
 * WindowContent — mounts the REAL existing screen inside a window.
 *
 * Each window gets its own MemoryRouter (initialEntries=[win.path]) rendering
 * the same `superAdminRoutes` the tab system uses, so the screen runs exactly
 * as today. This mirrors TabContentRenderer in Routes/Appcontainer/index.jsx.
 *
 * In-window navigation is handled by WindowNavigationTracker (below):
 *  - navigating to a detail page (e.g. /sales/123) opens it as a NEW window and
 *    snaps this window back to its base path (matches the tab behavior);
 *  - any other in-window navigation just records the new path on the window.
 * Screens that open details via dispatch(openTab(...)) are handled separately by
 * the webtopBridge middleware. Together these cover both navigation styles.
 *
 * An ErrorBoundary wraps the screen so one failing module can't take down the
 * whole desktop.
 */

const WindowNavigationTracker = ({ win }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Read the store lazily (inside the effect) instead of subscribing to the
  // full sales/clients/employees arrays. Subscribing via useSelector would
  // cause this component — and every other open window — to re-render
  // whenever any record changes, even though we only need the data at the
  // moment of navigation.
  const store = useStore();
  const isFirst = useRef(true);

  // Base path = first segment of the window's initial path (stable for its life).
  const basePath = `/${win.path.replace(/^\//, "").split("/")[0] || ""}`;
  const baseRef = useRef(basePath);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const path = location.pathname;
    if (path === baseRef.current) return;

    const baseSegs = baseRef.current.replace(/^\//, "").split("/").filter(Boolean);
    const newSegs = path.replace(/^\//, "").split("/").filter(Boolean);
    const isDetailNav =
      newSegs.length > baseSegs.length && newSegs.length >= 2 && newSegs[0] === baseSegs[0];

    if (isDetailNav) {
      const entity = newSegs[0].charAt(0).toUpperCase() + newSegs[0].slice(1);
      const recordId = newSegs[1];
      let title = `${entity} Detail`;
      // Read lazily — no persistent subscription to these large arrays.
      const s = store.getState();
      if (newSegs[0] === "sales") {
        const lead = findLeadInLists(s.sales, recordId);
        if (lead?.agencyName) title = lead.agencyName;
      } else if (newSegs[0] === "clients") {
        const client = s.clients?.clients?.find((c) => c.id === recordId);
        if (client?.clientName) title = client.clientName;
      } else if (newSegs[0] === "employees") {
        const emp = s.employee?.AllEmployees?.find((e) => e.id === recordId);
        if (emp?.firstname) title = `${emp.firstname}${emp.lastname ? " " + emp.lastname : ""}`;
      }
      dispatch(openWindow({ path, title, deduplicate: true }));
      navigate(baseRef.current, { replace: true });
    } else {
      dispatch(updateWindowPath({ id: win.id, path }));
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const WindowNotFound = () => (
  <div style={{ padding: 24, color: "#5a6270" }}>Page not found.</div>
);

const WindowContentBase = ({ win, routes = [] }) => {
  const initialPath = win.path.startsWith("/") ? win.path : `/${win.path}`;

  return (
    <NavigationContextReset>
      <MemoryRouter initialEntries={[initialPath]} initialIndex={0}>
        <WindowNavigationTracker win={win} />
        <ErrorBoundary>
          <Routes>
            {routes.map((item) => (
              <Route key={item.id} path={item.path} element={item.element} />
            ))}
            <Route path="*" element={<WindowNotFound />} />
          </Routes>
        </ErrorBoundary>
      </MemoryRouter>
    </NavigationContextReset>
  );
};

/**
 * Memoized so a window's (heavy) screen mounts once and does NOT re-render when
 * the window is dragged/resized/focused/minimized — only its id or the routes
 * table matter. Window geometry/focus live in the chrome layer; navigation
 * inside the window is handled by its own MemoryRouter. This keeps the desktop
 * fast with many windows open (Phase 15).
 */
const WindowContent = React.memo(
  WindowContentBase,
  (prev, next) => prev.win.id === next.win.id && prev.routes === next.routes
);

export { WindowNavigationTracker, WindowContentBase };
export default WindowContent;
